/**
 * Module-level Postgres fetch functions for every single-order domain.
 * @module dataAccumulator/singleOrderDomainFetchers
 */
import { Authorization } from "../../data-model/AuthorizationModel";
import { SiblingOrdersResponse } from "../../data-model/siblingOrdersModel";
import { WorkflowConfig } from "../../data-model/workflowConfig";
import { SubmissionDataResponse } from "../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { createPostgresDomainFetch } from "./createPostgresDomainFetch";
import { SINGLE_ORDER_DOMAIN_TABLE_NAMES } from "./singleOrderDomainConfig";
import {
  AuthOnFileDetailsResponse,
  ClinicalConditionsResponse,
  ClinicalDataResponse,
  CommentsDataResponse,
  CoverageDetailsResponse,
  GeneralOrderDetailsResponse,
  ReviewsDataResponse,
  SingleOrderDomainInstances,
} from "./types";

/**
 * Signature of an abortable single-order domain fetch.
 *
 * @template T - Response payload for the domain's Postgres endpoint
 */
type SingleOrderDomainFetchFn<T> = (
  signal: AbortSignal,
  orderId: string,
  orgId: string,
) => Promise<T>;

/** Domain fetch functions keyed by {@link SingleOrderDomainInstances} id. */
type SingleOrderDomainFetchers = {
  [DomainId in keyof SingleOrderDomainInstances]: SingleOrderDomainFetchFn<
    SingleOrderDomainResponses[DomainId]
  >;
};

/** Response payload type for each domain id. */
interface SingleOrderDomainResponses {
  generalOrderDetails: GeneralOrderDetailsResponse;
  authOnFileDetails: AuthOnFileDetailsResponse;
  clinicalData: ClinicalDataResponse;
  commentsData: CommentsDataResponse;
  coverageDetails: CoverageDetailsResponse;
  workflowConfig: WorkflowConfig;
  reviewsData: ReviewsDataResponse;
  clinicalConditions: ClinicalConditionsResponse;
  authorizationData: Authorization;
  submissionData: SubmissionDataResponse;
  siblingOrders: SiblingOrdersResponse;
}

/**
 * Fetch functions for every Postgres single-order domain, created once at
 * module load.
 *
 * Each entry only depends on its (constant) `table_name`, so building them
 * here rather than inside a component keeps their identity stable for the
 * lifetime of the app. `useAbortableFetch` memoises `runFetch` on the fetch
 * function it receives, so recreating these during render would give every
 * `runFetch` — and therefore the accumulator's `fetchAll` — a new identity on
 * each render, re-triggering any effect that depends on it.
 */
export const SINGLE_ORDER_DOMAIN_FETCHERS: SingleOrderDomainFetchers = {
  generalOrderDetails: createPostgresDomainFetch<GeneralOrderDetailsResponse>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.generalOrderDetails,
  ),
  authOnFileDetails: createPostgresDomainFetch<AuthOnFileDetailsResponse>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.authOnFileDetails,
  ),
  clinicalData: createPostgresDomainFetch<ClinicalDataResponse>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.clinicalData,
  ),
  commentsData: createPostgresDomainFetch<CommentsDataResponse>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.commentsData,
  ),
  coverageDetails: createPostgresDomainFetch<CoverageDetailsResponse>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.coverageDetails,
  ),
  workflowConfig: createPostgresDomainFetch<WorkflowConfig>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.workflowConfig,
  ),
  reviewsData: createPostgresDomainFetch<ReviewsDataResponse>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.reviewsData,
  ),
  clinicalConditions: createPostgresDomainFetch<ClinicalConditionsResponse>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.clinicalConditions,
  ),
  authorizationData: createPostgresDomainFetch<Authorization>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.authorizationData,
  ),
  submissionData: createPostgresDomainFetch<SubmissionDataResponse>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.submissionData,
  ),
  siblingOrders: createPostgresDomainFetch<SiblingOrdersResponse>(
    SINGLE_ORDER_DOMAIN_TABLE_NAMES.siblingOrders,
  ),
};
